import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";
import { currentBucketKey, formatGrainTick, freshnessBoundMonth, monthOffset, type GrainValue } from "@/lib/time-range";

import type { SeriesPoint } from "../api";
import type { Grain } from "../format";
import { PROVISIONAL_TEXT, formatRatio, isProvisionalMonth } from "../format";

/** The trend tooltip's series name; a provisional month says so. Exported for tests. */
export function trendSeriesLabel(row: unknown): string {
  const provisional = typeof row === "object" && row !== null && (row as { provisional?: unknown }).provisional === true;
  return provisional ? "Profit per GBP 1 (provisional)" : "Profit per GBP 1";
}

const GRAIN_NOUN: Record<Grain, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

// W1: when the plotted monthly series still ends before the freshness bound
// (one month before `reference`'s own month), state the boundary rather than
// silently drawing a chart that quietly stops early. The producer may only
// emit closed months (D-2 intentional debt) - this caption is the honest
// surface for that case either way.
function boundaryCaption(points: SeriesPoint[], grain: Grain, reference?: Date): string | undefined {
  if (grain !== "monthly" || !reference || points.length === 0) return undefined;
  const last = points.at(-1)?.m;
  if (!last) return undefined;
  const bound = freshnessBoundMonth(reference);
  if (last >= bound) {
    const provisional = points.filter((point) => isProvisionalMonth(point, reference)).map((point) => point.m);
    return provisional.length > 0 ? `${provisional.join(" and ")}: ${PROVISIONAL_TEXT}` : undefined;
  }
  const current = currentBucketKey("monthly", reference);
  const pending: string[] = [];
  let cursor = monthOffset(last, 1);
  while (cursor <= current) {
    pending.push(cursor.slice(5));
    cursor = monthOffset(cursor, 1);
  }
  if (pending.length === 0) return undefined;
  return `Closed months through ${last}; ${pending.join(" and ")} pending close`;
}

/**
 * The ratio over time.
 *
 * Charting goes through `ChartFrame` - the one charting seam (I-7). A module
 * never picks its own library, which is exactly how three chart idioms ended up
 * across the estate this portal exists to end.
 *
 * The empty message names the GRAIN. "No data" on a daily view, when weekly has
 * plenty, sends a reader looking for an outage that is really an unbackfilled
 * series.
 */
export function YieldTrend({
  points,
  grain,
  reference,
}: {
  points: SeriesPoint[];
  grain: Grain;
  reference?: Date;
}) {
  const data = points.map((point) => ({
    period: point.m,
    value: point.v,
    provisional: grain === "monthly" && isProvisionalMonth(point, reference),
  }));
  const caption = boundaryCaption(points, grain, reference);

  return (
    <ChartFrame
      title={`Profit yield by ${GRAIN_NOUN[grain]}`}
      titleVisible
      subtitle={caption}
      pointCount={data.length}
      emptyMessage={`No ${grain} points yet - the producer has not backfilled this grain. Try another period.`}
    >
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          tickFormatter={(value: string) => formatGrainTick(value, grain as GrainValue)}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          tickFormatter={(value: number) => formatRatio(value)}
          width={48}
        />
        <Tooltip
          // Recharts types a tooltip value as possibly-undefined. Coercing it
          // to a number would print "0.00" for a point that has no value -
          // a fabricated measurement - so a non-numeric value renders "n/a"
          // through the same formatter every other figure uses.
          formatter={(value: unknown, _name: unknown, item: { payload?: unknown }) => [
            formatRatio(typeof value === "number" ? value : null),
            trendSeriesLabel(item?.payload),
          ]}
          cursor={{ strokeDasharray: "3 3" }}
          contentStyle={{
            background: "var(--color-ks-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-ks-sm)",
            fontSize: 12,
          }}
        />
        <ReferenceLine y={0} stroke="var(--color-ks-grey-1)" />
        <Area
          type="linear"
          dataKey="value"
          stroke="none"
          fill="var(--color-ks-purple)"
          fillOpacity={0.12}
          // The Area shares dataKey="value" with the Line below it purely for
          // the fill - without this the tooltip gets two payload entries for
          // the same figure and prints it twice.
          tooltipType="none"
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="value"
          stroke="var(--color-ks-purple)"
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartFrame>
  );
}
